import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import {
  Box,
  Button,
  Badge,
  Card,
  Checkbox,
  Group,
  Stack,
  Text,
  TextInput,
  Textarea,
  PasswordInput,
  Switch,
  NumberInput,
  Tabs,
  Table,
  Loader
} from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import { Page, StatCard, AmmoScoreBadge, ConfirmModal } from '../../app/common/ui'
import { useStore } from '../../app/stores/store'
import { format } from 'date-fns'
import { toast } from 'react-toastify'

const FLAG_STATUS: Record<number, string> = { 0: 'Open', 1: 'Resolved', 2: 'Dismissed' }

const RESET_PWD_INPUT_STYLES = {
  input: {
    background: '#21262D',
    border: '1.5px solid #484F58',
    color: '#E6EDF3'
  }
} as const

export default observer(function AdminUserDetailPage () {
  const { username } = useParams<{ username: string }>()
  const history = useHistory()
  const { adminStore, userStore } = useStore()
  const u = adminStore.selectedUser

  const [displayName, setDisplayName] = useState('')
  const [email, setEmail] = useState('')
  const [bio, setBio] = useState('')
  const [isAdmin, setIsAdmin] = useState(false)
  const [isSuspended, setIsSuspended] = useState(false)
  const [suspendedReason, setSuspendedReason] = useState('')
  const [suspendedUntil, setSuspendedUntil] = useState<Date | null>(null)
  const [delta, setDelta] = useState(0)
  const [detail, setDetail] = useState('')
  const [isVisibleToUser, setIsVisibleToUser] = useState(false)
  const [adjustModalOpen, setAdjustModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [resetNewPassword, setResetNewPassword] = useState('')
  const [resetConfirmPassword, setResetConfirmPassword] = useState('')
  const [resetPwdError, setResetPwdError] = useState('')
  const [resetPwdModalOpen, setResetPwdModalOpen] = useState(false)

  useEffect(() => {
    if (!userStore.user?.isAdmin) {
      history.replace('/not-found')
      return
    }
    if (username) adminStore.loadUser(username)
  }, [userStore.user?.isAdmin, username])

  useEffect(() => {
    if (u) {
      setDisplayName(u.displayName ?? '')
      setEmail(u.email ?? '')
      setBio(u.bio ?? '')
      setIsAdmin(u.isAdmin)
      setIsSuspended(u.isSuspended)
      setSuspendedReason(u.suspendedReason ?? '')
      setSuspendedUntil(u.suspendedUntil ? new Date(u.suspendedUntil) : null)
    }
  }, [u])

  const handleSaveProfile = async () => {
    if (!username) return
    try {
      await adminStore.editUser(username, {
        displayName: displayName || undefined,
        email: email || undefined,
        bio: bio || undefined,
        isAdmin,
        isSuspended,
        suspendedReason: isSuspended ? suspendedReason : undefined,
        suspendedUntil: isSuspended && suspendedUntil ? suspendedUntil.toISOString() : null
      })
      toast.success('Profile updated')
    } catch (err) {
      toast.error('Failed to update profile')
    }
  }

  const handleAdjustReputation = () => {
    if (!username || detail.trim().length < 10) {
      toast.error('Detail must be at least 10 characters')
      return
    }
    setAdjustModalOpen(true)
  }

  const confirmAdjust = async () => {
    if (!username || delta === 0) return
    try {
      await adminStore.adjustReputation(username, delta, detail.trim(), isVisibleToUser)
      toast.success('Reputation adjusted')
      setAdjustModalOpen(false)
      setDelta(0)
      setDetail('')
      setIsVisibleToUser(false)
    } catch (err) {
      toast.error('Failed to adjust reputation')
    }
  }

  const confirmDelete = async () => {
    if (!username) return
    try {
      await adminStore.deleteUser(username)
      setDeleteModalOpen(false)
      toast.success('Account deleted')
      history.push('/admin/users')
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'message' in err ? String((err as { message: string }).message) : 'Failed to delete account'
      toast.error(message)
    }
  }

  const validateResetPasswordFields = (): boolean => {
    setResetPwdError('')
    if (!resetNewPassword.trim()) {
      setResetPwdError('New password is required.')
      return false
    }
    if (resetNewPassword.length < 6) {
      setResetPwdError('Password must be at least 6 characters.')
      return false
    }
    if (!resetConfirmPassword.trim()) {
      setResetPwdError('Confirm password is required.')
      return false
    }
    if (resetNewPassword !== resetConfirmPassword) {
      setResetPwdError('Passwords do not match.')
      return false
    }
    return true
  }

  const handleOpenResetPwdModal = () => {
    if (!validateResetPasswordFields()) return
    setResetPwdModalOpen(true)
  }

  const confirmResetPassword = async () => {
    if (!username || !validateResetPasswordFields()) return
    try {
      await adminStore.resetPassword(username, {
        newPassword: resetNewPassword,
        confirmPassword: resetConfirmPassword
      })
      toast.success('Password reset successfully.')
      setResetNewPassword('')
      setResetConfirmPassword('')
      setResetPwdError('')
      setResetPwdModalOpen(false)
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } }
      const detail = ax.response?.data?.detail
      setResetPwdError(typeof detail === 'string' ? detail : 'Failed to reset password.')
    }
  }

  if (!userStore.user?.isAdmin) return null
  if (adminStore.loadingDetail || !username) {
    return (
      <Page title="User">
        <Loader />
      </Page>
    )
  }
  if (!u) {
    return (
      <Page title="User">
        <Text color="dimmed">User not found.</Text>
      </Page>
    )
  }

  const isSelf = userStore.user?.username?.toLowerCase() === u.userName?.toLowerCase()
  const afterScore = (u.ammoScore ?? 0) + delta

  return (
    <Page
      title=""
      breadcrumbs={[
        { label: 'Admin', href: '/admin/users' },
        { label: 'User Management', href: '/admin/users' },
        { label: u.displayName || u.userName }
      ]}
    >
      <Stack spacing="lg">
        {(u.spamCandidateCount ?? 0) > 0 && (
          <Box
            sx={{
              padding: 12,
              borderRadius: 8,
              backgroundColor: 'var(--mantine-color-red-1)',
              border: '1px solid var(--mantine-color-red-3)'
            }}
          >
            <Text size="sm" weight={600} color="red">
              ⚠ This user has content flagged as spam-candidate ({u.spamCandidateCount} items)
            </Text>
          </Box>
        )}
        <Group position="apart" align="flex-start">
          <Box>
            <Text size="lg" weight={600}>
              {u.displayName || u.userName}
            </Text>
            {!isSelf && (
              <Button color="red" variant="outline" size="xs" mt="sm" onClick={() => setDeleteModalOpen(true)}>
                Delete account
              </Button>
            )}
            <Text size="sm" color="dimmed">
              @{u.userName}
            </Text>
            <Text size="sm" color="dimmed">
              {u.email}
            </Text>
            <Group spacing="xs" mt="sm">
              <AmmoScoreBadge tier={u.ammoTier} score={u.ammoScore} size="lg" />
              {u.isSuspended
                ? (
                <Badge color="red">Suspended</Badge>
                  )
                : (
                <Badge color="green">Active</Badge>
                  )}
              {u.isAdmin && <Badge color="violet">Admin</Badge>}
              {u.createdOn && (
                <Text size="xs" color="dimmed">
                  Joined {format(new Date(u.createdOn), 'MMM d, yyyy')}
                </Text>
              )}
            </Group>
          </Box>
        </Group>

        <Group spacing="md">
          <StatCard label="AmmoScore" value={u.ammoScore} />
          <StatCard label="Followers" value={u.followersCount} />
          <StatCard label="Posts" value={u.postCount} />
          <StatCard label="Parts" value={u.partCount} />
          <StatCard label="Builds" value={u.buildCount} />
          <StatCard label="Flags Upheld Against" value={u.flagsUpheldAgainstUser} />
        </Group>

        <Card withBorder padding="md">
          <Text weight={600} mb="sm">
            Edit profile
          </Text>
          <Stack spacing="sm">
            <TextInput label="Display name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
            <TextInput label="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
            <Textarea label="Bio" value={bio} onChange={(e) => setBio(e.target.value)} minRows={2} />
            <Switch
              label="Admin"
              checked={isAdmin}
              onChange={(e) => setIsAdmin(e.currentTarget.checked)}
              disabled={isSelf}
            />
            <Switch
              label="Suspended"
              checked={isSuspended}
              onChange={(e) => setIsSuspended(e.currentTarget.checked)}
            />
            {isSuspended && (
              <>
                <TextInput
                  label="Suspension reason"
                  value={suspendedReason}
                  onChange={(e) => setSuspendedReason(e.target.value)}
                  placeholder="Reason for suspension"
                />
                <DatePickerInput
                  label="Suspended until"
                  value={suspendedUntil}
                  onChange={setSuspendedUntil}
                  clearable
                />
              </>
            )}
            <Button onClick={handleSaveProfile} loading={adminStore.submitting}>
              Save
            </Button>
          </Stack>
        </Card>

        <Card withBorder padding="md">
          <Text weight={600} mb="sm">
            Reputation adjustment
          </Text>
          <Stack spacing="sm">
            <NumberInput
              label="Delta"
              value={delta}
              onChange={(v) => setDelta(typeof v === 'string' ? parseInt(v, 10) || 0 : v ?? 0)}
              placeholder="e.g. 10 or -5"
            />
            <Textarea
              label="Detail (min 10 characters)"
              value={detail}
              onChange={(e) => setDetail(e.target.value)}
              placeholder="e.g. Goodwill credit — account recovery after data migration"
              minRows={2}
            />
            <Checkbox
              label="Show this adjustment to the user on their profile"
              checked={isVisibleToUser}
              onChange={(e) => setIsVisibleToUser(e.currentTarget.checked)}
            />
            <Text size="sm" color="dimmed">
              Current: {u.ammoScore} → After: {afterScore}
            </Text>
            <Button
              color="yellow"
              onClick={handleAdjustReputation}
              disabled={delta === 0 || detail.trim().length < 10 || adminStore.submitting}
            >
              Adjust reputation
            </Button>
          </Stack>
        </Card>

        {userStore.user?.isAdmin && (
          <Card withBorder padding="md">
            <Text
              sx={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 10,
                textTransform: 'uppercase',
                letterSpacing: '1.5px',
                color: 'var(--gs-muted)'
              }}
              mb="md"
            >
              RESET USER PASSWORD
            </Text>
            <Stack spacing="sm">
              <Group grow align="flex-start">
                <PasswordInput
                  label="New Password"
                  placeholder="New password"
                  value={resetNewPassword}
                  onChange={(e) => {
                    setResetNewPassword(e.currentTarget.value)
                    if (resetPwdError) setResetPwdError('')
                  }}
                  styles={RESET_PWD_INPUT_STYLES}
                />
                <PasswordInput
                  label="Confirm Password"
                  placeholder="Confirm password"
                  value={resetConfirmPassword}
                  onChange={(e) => {
                    setResetConfirmPassword(e.currentTarget.value)
                    if (resetPwdError) setResetPwdError('')
                  }}
                  styles={RESET_PWD_INPUT_STYLES}
                />
              </Group>
              {resetPwdError && (
                <Text size="sm" color="red">
                  {resetPwdError}
                </Text>
              )}
              <Button
                fullWidth
                onClick={handleOpenResetPwdModal}
                disabled={adminStore.submitting}
                sx={{
                  backgroundColor: '#F85149',
                  color: '#fff',
                  border: 'none',
                  '&:hover': { backgroundColor: '#da3633' }
                }}
              >
                Reset Password
              </Button>
            </Stack>
          </Card>
        )}

        <ConfirmModal
          opened={adjustModalOpen}
          title="Confirm reputation adjustment"
          message={`You are adjusting ${u.userName} AmmoScore by ${delta}. This action is logged. Reason: ${detail}`}
          confirmLabel="Adjust"
          variant="warning"
          onConfirm={confirmAdjust}
          onClose={() => setAdjustModalOpen(false)}
        />

        <ConfirmModal
          opened={deleteModalOpen}
          title="Delete account"
          message={`Permanently delete the account @${u.userName}? This will remove their profile, content, and all related data. This cannot be undone.`}
          confirmLabel="Delete account"
          variant="danger"
          onConfirm={confirmDelete}
          onClose={() => setDeleteModalOpen(false)}
        />

        <ConfirmModal
          opened={resetPwdModalOpen}
          title="Reset password"
          message={`Are you sure you want to reset ${u.displayName || u.userName}'s password? They will need to be notified of their new password manually.`}
          confirmLabel="Reset password"
          variant="danger"
          onConfirm={confirmResetPassword}
          onClose={() => setResetPwdModalOpen(false)}
        />

        <Tabs defaultValue="about">
          <Tabs.List>
            <Tabs.Tab value="about">About (signals)</Tabs.Tab>
            <Tabs.Tab value="flagsFiled">Flags filed</Tabs.Tab>
            <Tabs.Tab value="flagsAgainst">Flags against</Tabs.Tab>
            <Tabs.Tab value="modRoles">Moderator roles</Tabs.Tab>
            <Tabs.Tab value="adminLog">Admin log</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="about" pt="md">
            {u.signalCounters && (
              <Group spacing="md">
                <StatCard label="Post votes received" value={u.signalCounters.postVotesReceived} />
                <StatCard label="Post votes lost" value={u.signalCounters.postVotesLost} />
                <StatCard label="Part votes received" value={u.signalCounters.partVotesReceived} />
                <StatCard label="Part votes lost" value={u.signalCounters.partVotesLost} />
                <StatCard label="Build votes received" value={u.signalCounters.buildVotesReceived} />
                <StatCard label="Build votes lost" value={u.signalCounters.buildVotesLost} />
                <StatCard label="Approved parts" value={u.signalCounters.approvedPartsCount} />
                <StatCard label="Approved models" value={u.signalCounters.approvedModelsCount} />
              </Group>
            )}
          </Tabs.Panel>
          <Tabs.Panel value="flagsFiled" pt="md">
            <Table striped>
              <thead>
                <tr>
                  <th>Target</th>
                  <th>Reason</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {(u.recentFlags || []).map((f) => (
                  <tr key={f.id}>
                    <td>{f.targetType} / {f.targetId}</td>
                    <td>{f.reason}</td>
                    <td>{FLAG_STATUS[f.status] ?? f.status}</td>
                    <td>{format(new Date(f.createdAt), 'MMM d, yyyy HH:mm')}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {(!u.recentFlags || u.recentFlags.length === 0) && <Text color="dimmed">No flags filed.</Text>}
          </Tabs.Panel>
          <Tabs.Panel value="flagsAgainst" pt="md">
            <Table striped>
              <thead>
                <tr>
                  <th>Target</th>
                  <th>Reason</th>
                  <th>Resolution</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {(u.recentFlagsAgainst || []).map((f) => (
                  <tr key={f.id}>
                    <td>{f.targetType} / {f.targetId}</td>
                    <td>{f.reason}</td>
                    <td>{f.resolution ?? '—'}</td>
                    <td>{format(new Date(f.createdAt), 'MMM d, yyyy HH:mm')}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {(!u.recentFlagsAgainst || u.recentFlagsAgainst.length === 0) && <Text color="dimmed">No flags against.</Text>}
          </Tabs.Panel>
          <Tabs.Panel value="modRoles" pt="md">
            <Stack spacing="xs">
              {(u.moderatorRoles || []).map((r, i) => (
                <Text key={i}>
                  <Badge size="sm" variant="light">{r.scope}</Badge> granted {format(new Date(r.grantedAt), 'MMM d, yyyy')}
                </Text>
              ))}
              {(!u.moderatorRoles || u.moderatorRoles.length === 0) && <Text color="dimmed">No moderator roles.</Text>}
            </Stack>
          </Tabs.Panel>
          <Tabs.Panel value="adminLog" pt="md">
            <Table striped>
              <thead>
                <tr>
                  <th>Action</th>
                  <th>Detail</th>
                  <th>By</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {(u.recentAdminActions || []).map((a) => (
                  <tr key={a.id}>
                    <td>{a.action}</td>
                    <td style={{ maxWidth: 400 }}>{a.detail}</td>
                    <td>{a.adminUsername}</td>
                    <td>{format(new Date(a.createdAt), 'MMM d, yyyy HH:mm')}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {(!u.recentAdminActions || u.recentAdminActions.length === 0) && <Text color="dimmed">No admin actions.</Text>}
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Page>
  )
})
